import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useConsoleI18n } from './bind';

/** One series with its reference lines (a container's request and limit; null = not set). */
export interface UsageSeries {
  points: { t: string; v: number }[];
  request: number | null;
  limit: number | null;
}

const H = 168;
const PAD = { top: 10, right: 12, bottom: 22, left: 56 };

/** Tracks an element's content width (charts draw at real pixels so text is never stretched). */
export function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/** 1, 2, 2.5, 5 × 10ⁿ steps so the axis reads in round numbers. */
export function niceTicks(max: number, count = 4): number[] {
  if (!(max > 0)) return [0];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const ticks: number[] = [];
  for (let v = 0; v <= max + step * 0.001; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] < max) ticks.push(ticks[ticks.length - 1] + step);
  return ticks;
}

const timeLabel = (t: number, long: boolean) =>
  new Date(t).toLocaleString(
    undefined,
    long
      ? { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
      : { hour: '2-digit', minute: '2-digit' }
  );

const pct = (v: number, of: number | null) => (of && of > 0 ? `${Math.round((v / of) * 100)} %` : null);

/**
 * One usage series over time with the container's request and limit as dashed reference lines (direct-labelled, one
 * y-axis). A crosshair snaps to the nearest sample; arrow keys move it when the chart has focus. Gaps longer than two
 * steps (restart, no samples) break the line instead of bridging it.
 */
export function UsageChart({
  title,
  series,
  from,
  to,
  stepSeconds,
  format,
  limitLabel,
}: {
  title: string;
  series: UsageSeries;
  from: string;
  to: string;
  stepSeconds: number;
  format: (v: number) => string;
  /** Not a container: no request, and the limit (if any) under this name. */
  limitLabel?: string;
}) {
  const { t } = useConsoleI18n();
  const [wrap, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const t0 = Date.parse(from);
  const t1 = Date.parse(to);
  const pts = useMemo(() => series.points.map((p) => ({ t: Date.parse(p.t), v: p.v })), [series.points]);
  const values = pts.map((p) => p.v);
  const current = pts.length ? pts[pts.length - 1].v : null;
  const peak = values.length ? Math.max(...values) : null;
  const avg = values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
  const ticks = niceTicks(Math.max(peak ?? 0, series.request ?? 0, series.limit ?? 0) * 1.05);
  const yMax = ticks[ticks.length - 1] || 1;
  const long = t1 - t0 > 24 * 3600_000;

  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = H - PAD.top - PAD.bottom;
  const x = (t: number) => PAD.left + ((t - t0) / Math.max(1, t1 - t0)) * plotW;
  const y = (v: number) => PAD.top + plotH - (v / yMax) * plotH;

  const path = useMemo(() => {
    let d = '';
    let prev: number | null = null;
    for (const p of pts) {
      const gap = prev !== null && p.t - prev > stepSeconds * 2000;
      d += `${d && !gap ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`;
      prev = p.t;
    }
    return d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pts, width, yMax, t0, t1, stepSeconds]);

  const nearest = (clientX: number, rect: DOMRect) => {
    if (!pts.length) return null;
    const t = t0 + ((clientX - rect.left - PAD.left) / Math.max(1, plotW)) * (t1 - t0);
    let best = 0;
    for (let i = 1; i < pts.length; i++) if (Math.abs(pts[i].t - t) < Math.abs(pts[best].t - t)) best = i;
    return best;
  };
  const onMove = (e: PointerEvent<SVGSVGElement>) =>
    setHover(nearest(e.clientX, e.currentTarget.getBoundingClientRect()));
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (!pts.length) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const d = e.key === 'ArrowLeft' ? -1 : 1;
      setHover((h) => Math.min(pts.length - 1, Math.max(0, (h ?? pts.length - 1) + d)));
    } else if (e.key === 'Escape') setHover(null);
  };

  const limitName = limitLabel?.toLowerCase() ?? t('chart.limit').toLowerCase();
  const refs = [
    { key: 'limit', label: limitName, value: series.limit },
    { key: 'request', label: t('chart.request').toLowerCase(), value: series.request },
  ].filter((r): r is { key: string; label: string; value: number } => r.value != null && r.value > 0);
  const h = hover != null ? pts[hover] : null;
  const tipLeft = h ? Math.min(Math.max(x(h.t), 90), Math.max(90, width - 90)) : 0;

  return (
    <figure className="nyk-usage">
      <figcaption className="nyk-usage-head">
        <h3>{title}</h3>
        <dl className="nyk-usage-stats">
          <div>
            <dt>{t('chart.now')}</dt>
            <dd>{current != null ? format(current) : '—'}</dd>
          </div>
          <div>
            <dt>{t('chart.avg')}</dt>
            <dd>{avg != null ? format(avg) : '—'}</dd>
          </div>
          <div>
            <dt>{t('chart.peak')}</dt>
            <dd>{peak != null ? format(peak) : '—'}</dd>
          </div>
          {limitLabel === undefined ? (
            <>
              <div>
                <dt>{t('chart.request')}</dt>
                <dd>{series.request ? format(series.request) : t('chart.notSet')}</dd>
              </div>
              <div>
                <dt>{t('chart.limit')}</dt>
                <dd>{series.limit ? format(series.limit) : t('chart.notSet')}</dd>
              </div>
            </>
          ) : (
            series.limit != null && (
              <div>
                <dt>{limitLabel}</dt>
                <dd>{format(series.limit)}</dd>
              </div>
            )
          )}
        </dl>
      </figcaption>
      <div className="nyk-usage-plot" ref={wrap}>
        {!pts.length ? (
          <div className="nyk-usage-empty">{t('chart.noSamples')}</div>
        ) : (
          width > 0 && (
            <svg
              width={width}
              height={H}
              role="img"
              aria-label={t('chart.overTime', {
                title,
                now: current != null ? format(current) : t('chart.unknown'),
                peak: peak != null ? format(peak) : t('chart.unknown'),
              })}
              tabIndex={0}
              onPointerMove={onMove}
              onPointerLeave={() => setHover(null)}
              onKeyDown={onKey}
              onBlur={() => setHover(null)}
            >
              {ticks.map((v) => (
                <g key={v}>
                  <line className="nyk-usage-grid" x1={PAD.left} x2={PAD.left + plotW} y1={y(v)} y2={y(v)} />
                  <text className="nyk-usage-axis" x={PAD.left - 8} y={y(v)} dy="0.32em" textAnchor="end">
                    {format(v)}
                  </text>
                </g>
              ))}
              {[t0, (t0 + t1) / 2, t1].map((t, i) => (
                <text
                  key={i}
                  className="nyk-usage-axis"
                  x={x(t)}
                  y={H - 6}
                  textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}
                >
                  {timeLabel(t, long)}
                </text>
              ))}
              {refs.map((r) => (
                <g key={r.key} className={`nyk-usage-ref nyk-usage-${r.key}`}>
                  <line x1={PAD.left} x2={PAD.left + plotW} y1={y(r.value)} y2={y(r.value)} />
                  <text x={PAD.left + plotW - 4} y={y(r.value) - 4} textAnchor="end">
                    {r.label}
                  </text>
                </g>
              ))}
              <path className="nyk-usage-line" d={path} />
              {h && (
                <g className="nyk-usage-cross">
                  <line x1={x(h.t)} x2={x(h.t)} y1={PAD.top} y2={PAD.top + plotH} />
                  <circle cx={x(h.t)} cy={y(h.v)} r={4} />
                </g>
              )}
            </svg>
          )
        )}
        {h && (
          <div className="nyk-usage-tip" style={{ left: tipLeft }} role="status">
            <strong>{format(h.v)}</strong>
            <span>{timeLabel(h.t, true)}</span>
            {pct(h.v, series.request) && <span>{pct(h.v, series.request)} of request</span>}
            {pct(h.v, series.limit) && (
              <span>
                {pct(h.v, series.limit)} of {limitName}
              </span>
            )}
          </div>
        )}
      </div>
    </figure>
  );
}
